import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { motion } from "framer-motion";

const features = [
  {
    title: "Built for Real Restaurant Ops",
    description:
      "VenFlows isn't a generic ERP. It's designed specifically for restaurants, cafés, and cloud kitchens—built by people who understand food, not just software.",
    icon: "restaurant",
  },
  {
    title: "End-to-End Control",
    description:
      "From raw material purchase to final sale at the cashier, VenFlows connects every dot. One system, one source of truth, zero blind spots.",
    icon: "hub",
  },
  {
    title: "Customizable to Your Workflow",
    description:
      "No two kitchens work the same. That's why we let you craft your own recipes, APIs, BoMs, roles, and even access levels. You're in control—not boxed in by rigid templates.",
    icon: "settings_suggest",
  },
  {
    title: "Cost & Waste Reduction at Its Core",
    description:
      "Everything you do inside VenFlows is tracked with expiry dates, cost changes, and batch-level precision. Stop losing money to expired stock and guesswork.",
    icon: "trending_down",
  },
  {
    title: "Scales as You Grow",
    description:
      "One branch or fifty, dine-in or delivery, VenFlows handles multi-branch, multi-warehouse, and multi-user setups effortlessly.",
    icon: "expand",
  },
  {
    title: "Real-Time Insights, Not Delayed Reports",
    description:
      "No more end-of-month surprises. Get real-time cost tracking, stock usage, and sales insights so you can make decisions that matter—fast.",
    icon: "insights",
  },
  {
    title: "Staff & Access Under Control",
    description:
      "Assign who sees what, who manages where, and who can change how. Total visibility, full accountability.",
    icon: "admin_panel_settings",
  },
];

const BackgroundPattern = () => (
  <Box
    sx={{
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: "hidden",
      opacity: 0.4,
      zIndex: 0,
    }}
  >
    <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern
          id="dots-pattern"
          x="0"
          y="0"
          width="24"
          height="24"
          patternUnits="userSpaceOnUse"
        >
          <circle cx="2" cy="2" r="1" fill="rgba(6, 182, 212, 0.3)" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#dots-pattern)" />
    </svg>
  </Box>
);

const FeatureCard: React.FC<{
  title: string;
  description: string;
  icon: string;
  index: number;
}> = ({ title, description, icon, index }) => {
  const theme = useTheme();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <Box
        sx={{
          p: 4,
          height: "100%",
          background: "rgba(255, 255, 255, 0.03)",
          backdropFilter: "blur(10px)",
          borderRadius: "16px",
          border: "1px solid rgba(255, 255, 255, 0.05)",
          position: "relative",
          overflow: "hidden",
          transition: "transform 0.3s ease",
          "&:hover": {
            transform: "translateY(-4px)",
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "2px",
            background: "linear-gradient(90deg, #06B6D4, transparent)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box
            sx={{
              width: "40px",
              height: "40px",
              borderRadius: "10px",
              background:
                "linear-gradient(135deg, rgba(6, 182, 212, 0.2) 0%, rgba(6, 182, 212, 0.1) 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 2,
            }}
          >
            <span
              className="material-icons"
              style={{ color: "#06B6D4", fontSize: "24px" }}
            >
              {icon}
            </span>
          </Box>
          <Typography
            variant="h6"
            sx={{
              color: "white",
              fontWeight: 600,
              fontSize: "1.125rem",
              fontFamily: "Montserrat",
            }}
          >
            {title}
          </Typography>
        </Box>
        <Typography
          sx={{
            color: "rgba(255, 255, 255, 0.7)",
            fontSize: "0.875rem",
            lineHeight: 1.7,
            fontFamily: "Montserrat",
          }}
        >
          {description}
        </Typography>
      </Box>
    </motion.div>
  );
};

export const ComparisonSection: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Box
      sx={{
        py: { xs: 10, md: 15 },
        background: "linear-gradient(135deg, #000B18 0%, #001E3C 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <BackgroundPattern />
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Typography
            variant="h2"
            align="center"
            sx={{
              fontSize: { xs: "2rem", md: "2.75rem" },
              fontWeight: 700,
              mb: 2,
              color: "white",
              fontFamily: "Montserrat",
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: -16,
                left: "50%",
                transform: "translateX(-50%)",
                width: "80px",
                height: "3px",
                background: "linear-gradient(90deg, #06B6D4, #22D3EE)",
                borderRadius: "2px",
              },
            }}
          >
            Why Choose VenFlows?
          </Typography>
          <Typography
            align="center"
            sx={{
              fontSize: { xs: "1rem", md: "1.125rem" },
              color: "rgba(255, 255, 255, 0.7)",
              maxWidth: "800px",
              mx: "auto",
              mb: 8,
              mt: 4,
              fontFamily: "Montserrat",
              lineHeight: 1.6,
            }}
          >
            Discover why leading restaurants and food businesses trust VenFlows
            for their operations management
          </Typography>
        </motion.div>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} key={index}>
              <FeatureCard {...feature} index={index} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};
